import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  adminMeSchema,
  adminSessionSchema,
  type AdminLoginInput,
  type AdminMe,
  type AdminSession,
} from '../schemas';
import { useToken } from './use-token';

/**
 * Auth du back-office plateforme : email + mot de passe, pas de code à usage
 * unique. Table, jeton et routes distincts de ceux des membres de cabinet
 * un jeton `user` est rejeté par ces routes.
 */
export function useAdminMe() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.adminAuth.me(),
    queryFn: () => api.get<AdminMe>('/admin/auth/me', { schema: adminMeSchema }),
    enabled: token !== null,
    staleTime: 5 * 60_000,
  });
}

export function useAdminLogin() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AdminLoginInput) =>
      api.post<AdminSession>('/admin/auth/login', input, { schema: adminSessionSchema }),
    onSuccess: ({ token, admin }) => {
      api.tokens.set(token);
      queryClient.setQueryData(queryKeys.adminAuth.me(), { admin });
    },
  });
}

export function useAdminLogout() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        await api.post('/admin/auth/logout');
      } finally {
        api.tokens.clear();
      }
    },
    onSettled: () => queryClient.clear(),
  });
}
